import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import html2canvas from 'html2canvas';

interface LomavisDownloadDomElementButtonProps {
  /** Ref to the DOM element to capture */
  elementRef: React.RefObject<HTMLElement>;
  /** Optional filename for the download */
  fileName?: string;
  /** Optional scaling factor for resolution */
  scale?: number;
  /** Optional function to override the download behavior */
  onDownload?: () => Promise<void>;
}

export const LomavisDownloadDomElementButton: React.FC<
  LomavisDownloadDomElementButtonProps
> = ({ elementRef, fileName = 'download', scale = 2, onDownload }) => {
  const [isLoading, setIsLoading] = useState(false);

  const defaultDownload = async () => {
    const original = elementRef.current;
    if (!original) return;

    await document.fonts.ready;

    const { width, height } = original.getBoundingClientRect();
    const clone = original.cloneNode(true) as HTMLElement;

    // Style and icon adjustments
    clone.style.width = `${width}px`;
    clone.style.height = `${height}px`;

    // Copy canvas content from original to clone
    const originalCanvases = original.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');

    originalCanvases.forEach((originalCanvas, index) => {
      if (index < cloneCanvases.length) {
        const cloneCanvas = cloneCanvases[index];
        const context = cloneCanvas.getContext('2d');
        if (context) {
          // Make sure dimensions match
          cloneCanvas.width = originalCanvas.width;
          cloneCanvas.height = originalCanvas.height;
          // Copy the content
          context.drawImage(originalCanvas, 0, 0);
        }
      }
    });

    clone.querySelectorAll('.fa-duotone').forEach((icon) => {
      icon.classList.remove('fa-duotone');
      icon.classList.add('fa-solid');
    });

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.zIndex = '-1';
    wrapper.style.opacity = '0';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.width = `${width}px`;
    wrapper.style.height = `${height}px`;
    wrapper.style.backgroundColor = '#ffffff'; // Ensure a white background
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      // Delay to ensure canvas elements (especially echarts) are fully rendered
      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(clone, {
        scale,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        backgroundColor: '#ffffff',
        width,
        height,
        scrollX: 0,
        scrollY: 0,
        windowWidth: width,
        windowHeight: height
      });

      const dataURL = canvas.toDataURL('image/jpeg', 1.0);
      const link = document.createElement('a');
      link.href = dataURL;
      const sanitizedFileName = fileName
        .trim()
        .replace(/[^\w-]+/g, '_') // replace all non-word/hyphen chars with "_"
        .replace(/_+/g, '_') // collapse multiple underscores
        .replace(/^_+|_+$/g, ''); // trim leading/trailing underscores

      link.download = `${sanitizedFileName}.jpeg`;
      link.click();
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  const handleClick = async () => {
    setIsLoading(true);
    try {
      if (onDownload) {
        await onDownload();
      } else {
        await defaultDownload();
      }
    } catch (err) {
      console.error('Download failed', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="light"
      className="bg-white btn-white"
      size="sm"
      data-html2canvas-ignore="true"
      onClick={() => {
        void handleClick();
      }}
      disabled={isLoading}
    >
      {isLoading ? (
        <Spinner animation="border" size="sm" className="me-1" />
      ) : (
        <i className="fa-duotone fa-arrow-down-to-line fs-4"></i>
      )}
    </Button>
  );
};
